import { useState } from "react";
import { useToast } from "@west-tennessee-consulting/ui";
import { useApolloClient } from "@apollo/client/react";
import { useAuthedFetch } from "@/lib/hooks/use-authed-fetch";
import type { Msp_DocumentsInsertInput, Msp_DocumentsUpdateInput } from "@/src/gql/graphql";
import { extractErrorMessage } from "@west-tennessee-consulting/api-errors";

/**
 * `create_msp_doc`/`update_msp_doc`'s zod schemas (createMspDocInputSchema /
 * updateMspDocInputSchema) use `.optional()`, not `.nullable()` — a `null`
 * (this form's convention for "clear the field") fails validation where the
 * GraphQL mutation happily accepted it. Omit rather than null, and drop
 * `created_by`/`updated_by`: the route stamps those from the session, not the
 * body (the GraphQL path set them client-side only because it had no
 * server-side identity to fall back on).
 */
function toWriteBody<T extends Record<string, unknown>>(input: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const [key, value] of Object.entries(input)) {
    if (key === "created_by" || key === "updated_by") continue;
    if (value === null) continue;
    (cleaned as Record<string, unknown>)[key] = value;
  }
  return cleaned;
}

interface MspDocWriteEnvelope {
  success?: boolean;
  document?: { id: string; title: string; viewInApp?: string };
  error?: string;
}

async function parseWriteResponse(response: Response): Promise<MspDocWriteEnvelope> {
  try {
    return (await response.json()) as MspDocWriteEnvelope;
  } catch {
    return {};
  }
}

/**
 * Create new MSP Document.
 *
 * Goes through `POST /api/workspace-addon/msp-docs` — the SAME
 * `makeCreateMspDocExecutor` the `create_msp_doc` Meridian chat tool and the
 * native apps use — rather than the raw `CreateMspDocumentsDocument` mutation.
 * That is not a transport preference: the executor also runs content-offload
 * (lib/msp-docs/content-offload.ts) and durable image-promotion
 * (lib/msp-docs/image-attachments.ts) after the write, and the direct GraphQL
 * mutation this replaced skipped both — a doc authored in the web form never
 * got either benefit.
 */
export function useCreateMspDocument() {
  const { toast } = useToast();
  const authedFetch = useAuthedFetch();
  const apolloClient = useApolloClient();
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (input: Msp_DocumentsInsertInput) => {
    setIsPending(true);
    try {
      const response = await authedFetch("/api/workspace-addon/msp-docs", {
        method: "POST",
        body: JSON.stringify(toWriteBody(input)),
      });
      const body = await parseWriteResponse(response);
      if (!response.ok || body.error || !body.document) {
        throw new Error(body.error || `Failed to create document (${response.status})`);
      }

      await apolloClient.refetchQueries({ include: ["GetMspDocumentss"] });

      toast({
        title: "Document Created",
        description: "Document has been created successfully.",
      });

      return body.document;
    } catch (error) {
      toast({
        title: "Error",
        description: extractErrorMessage(error, { fallback: "Failed to create document" }),
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync,
    isPending,
  };
}

/**
 * Update existing MSP Document. Same reasoning as `useCreateMspDocument` —
 * `PATCH /api/workspace-addon/msp-docs/[id]` runs the same executor
 * (`makeUpdateMspDocExecutor`) as `update_msp_doc`, so an edit made in the web
 * form gets content-offload / image-promotion too.
 */
export function useUpdateMspDocument() {
  const { toast } = useToast();
  const authedFetch = useAuthedFetch();
  const apolloClient = useApolloClient();
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async ({ id, input }: { id: string; input: Msp_DocumentsUpdateInput }) => {
    setIsPending(true);
    try {
      const response = await authedFetch(`/api/workspace-addon/msp-docs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(toWriteBody(input)),
      });
      const body = await parseWriteResponse(response);
      if (!response.ok || body.error || !body.document) {
        throw new Error(body.error || `Failed to update document (${response.status})`);
      }

      await apolloClient.refetchQueries({ include: ["GetMspDocumentss", "GetMspDocuments"] });

      toast({
        title: "Document Updated",
        description: "Document has been updated successfully.",
      });

      return body.document;
    } catch (error) {
      toast({
        title: "Error",
        description: extractErrorMessage(error),
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync,
    isPending,
  };
}
